/**
 * ProtocolGroup - Grupo de protocolos por capa
 */

import { motion, AnimatePresence } from 'framer-motion';
import { Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { cn, formatMinutes } from '@/core/utils';
import { getLayerConfig } from '@/core/constants';
import type { ProtocolGroup as ProtocolGroupType } from '@/core/types';
import { ProgressBar, Badge } from '@/ui/primitives';
import { ProtocolCard } from '@/features/protocols/components/ProtocolCard';

interface ProtocolGroupProps {
  group: ProtocolGroupType;
  isUnlocked: boolean;
}

export function ProtocolGroup({ group, isUnlocked }: ProtocolGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const config = getLayerConfig(group.layer);

  if (group.protocols.length === 0) {
    return null;
  }

  const progress =
    group.totalCount > 0
      ? Math.round((group.completedCount / group.totalCount) * 100)
      : 0;

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'rounded-xl border overflow-hidden',
        'transition-all duration-300',
        isUnlocked ? 'border-slate-800' : 'border-slate-800/50 opacity-60'
      )}
    >
      {/* Header de la capa */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        disabled={!isUnlocked}
        className={cn(
          'w-full flex items-center justify-between p-4',
          'transition-colors',
          isUnlocked
            ? cn(config.color.bgTransparent, 'hover:bg-opacity-50')
            : 'bg-slate-900/50 cursor-not-allowed'
        )}
      >
        <div className="flex items-center gap-3">
          {!isUnlocked && <Lock className="w-4 h-4 text-slate-600" />}

          <div className="text-left">
            <div className="flex items-center gap-2">
              <h2 className={cn('font-semibold', config.color.text)}>
                {config.name}
              </h2>
              <Badge variant={group.layer} size="xs">
                {group.completedCount}/{group.totalCount}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">{config.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className={cn('text-sm font-medium', config.color.text)}>
              {progress}%
            </p>
            <p className="text-xs text-slate-500">
              ~{formatMinutes(group.totalTime)}
            </p>
          </div>

          {isUnlocked && (
            isExpanded ? (
              <ChevronUp className="w-5 h-5 text-slate-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-500" />
            )
          )}
        </div>
      </button>

      {/* Barra de progreso */}
      <div className="px-4">
        <ProgressBar value={progress} layer={group.layer} size="sm" />
      </div>

      {/* Lista de protocolos */}
      <AnimatePresence>
        {isExpanded && isUnlocked && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-3">
              {group.protocols.map((protocol, index) => (
                <motion.div
                  key={protocol.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ProtocolCard protocol={protocol} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mensaje de capa bloqueada */}
      {!isUnlocked && (
        <div className="p-4 text-center">
          <p className="text-xs text-slate-600">
            Completa la capa anterior para desbloquear
          </p>
        </div>
      )}
    </motion.section>
  );
}
